import { useBridge } from '@web/adapter/useBridgeRuntime'
import { api } from '@web/api/client'
import { StructuredValue } from '@web/components/tools/StructuredContent'
import { agentMetadata } from '@web/state/acp'
import { useState } from 'react'

/** Advertised typed operations use the same schema-driven UI across providers. */
export function ExtensionActions() {
  const { snapshot, sessionId } = useBridge()
  const extensions = agentMetadata(snapshot.acp).extensions ?? {}
  return (
    <>
      {Object.entries(extensions)
        .filter(
          ([method, contract]) =>
            method.startsWith('_') && contract.phase !== 'before_session',
        )
        .map(([method, contract]) => (
          <ExtensionAction
            key={`${sessionId}:${method}`}
            method={method}
            schema={contract.requestSchema}
            sessionId={sessionId}
            outcomes={snapshot.acp.extensions}
            commandErrors={snapshot.bridge.commandErrors}
            disabled={
              snapshot.bridge.record?.migration_status === 'diagnostic' ||
              snapshot.hydrating ||
              snapshot.socket !== 'open'
            }
          />
        ))}
    </>
  )
}
function ExtensionAction({
  method,
  schema,
  sessionId,
  disabled,
  outcomes,
  commandErrors,
}: {
  method: string
  schema: Record<string, unknown>
  sessionId: string
  disabled: boolean
  outcomes: { seq: number; payload: Record<string, unknown> }[]
  commandErrors: { requestId?: string; error: string }[]
}) {
  const properties =
    schema.properties && typeof schema.properties === 'object'
      ? (Object.entries(schema.properties) as [
          string,
          Record<string, unknown>,
        ][])
      : []
  // The host supplies session identity. Fixed values and defaults come from
  // the advertised schema, so users enter only the operation's actual inputs.
  const defaults = Object.fromEntries(
    properties
      .filter(
        ([key, field]) =>
          key !== 'sessionId' &&
          (Object.hasOwn(field, 'const') || Object.hasOwn(field, 'default')),
      )
      .map(([key, field]) => [
        key,
        Object.hasOwn(field, 'const') ? field.const : field.default,
      ]),
  )
  const editable = properties.filter(
    ([key, field]) => key !== 'sessionId' && !Object.hasOwn(field, 'const'),
  )
  const fixed = Object.fromEntries(
    properties
      .filter(
        ([key, field]) => key !== 'sessionId' && Object.hasOwn(field, 'const'),
      )
      .map(([key, field]) => [key, field.const]),
  )
  const [text, setText] = useState(() => JSON.stringify(defaults, null, 2))
  const [fields, setFields] = useState<Record<string, unknown>>(defaults)
  const simple =
    schema.type === 'object' &&
    editable.every(([, field]) =>
      ['string', 'number', 'integer', 'boolean'].includes(String(field.type)),
    )
  const required = Array.isArray(schema.required) ? schema.required : []
  const [requestId, setRequestId] = useState<string>()
  const [localError, setError] = useState('')
  const outcome = [...outcomes]
    .reverse()
    .find(
      ({ payload }) =>
        payload.type === 'agent/extension' &&
        payload.method === method &&
        Object.hasOwn(payload, 'response') &&
        (!requestId || payload.request_id === requestId),
    )?.payload
  const error =
    localError ||
    (requestId
      ? commandErrors.find((item) => item.requestId === requestId)?.error
      : '')
  const busy = Boolean(requestId && !outcome && !error)
  const result = outcome?.response
  const submit = async () => {
    setError('')
    const id = crypto.randomUUID()
    setRequestId(id)
    try {
      const input = simple ? fields : JSON.parse(text)
      if (!input || typeof input !== 'object' || Array.isArray(input))
        throw new Error('Enter an object matching the operation fields.')
      const { sessionId: _sessionId, ...params } = {
        ...defaults,
        ...input,
        ...fixed,
      }
      await api.command(sessionId, {
        type: 'extension',
        method,
        params,
        request_id: id,
      })
    } catch (failure) {
      setError((failure as Error).message)
    }
  }
  return (
    <details className="my-3 rounded-control border border-line p-2">
      <summary>
        {typeof schema.title === 'string'
          ? schema.title
          : method.split('/').at(-1)?.replaceAll('_', ' ')}
      </summary>
      {typeof schema.description === 'string' && (
        <p className="my-2 text-muted">{schema.description}</p>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        {simple ? (
          editable.map(([key, field]) => (
            <label key={key} className="block my-2">
              {String(field.title ?? key)}
              {field.description ? (
                <span className="block text-faint">
                  {String(field.description)}
                </span>
              ) : null}
              {field.type === 'boolean' ? (
                <select
                  aria-label={String(field.title ?? key)}
                  className="control mt-1 w-full"
                  value={String(fields[key] ?? '')}
                  required={required.includes(key)}
                  disabled={disabled || busy}
                  onChange={(event) =>
                    setFields((current) => ({
                      ...current,
                      [key]: event.target.value === 'true',
                    }))
                  }
                >
                  <option value="">Choose</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : Array.isArray(field.enum) ? (
                <select
                  aria-label={String(field.title ?? key)}
                  className="control mt-1 w-full"
                  value={String(fields[key] ?? '')}
                  required={required.includes(key)}
                  disabled={disabled || busy}
                  onChange={(event) =>
                    setFields((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))
                  }
                >
                  <option value="">Choose</option>
                  {field.enum.map((value) => (
                    <option key={String(value)} value={String(value)}>
                      {String(value)}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  aria-label={String(field.title ?? key)}
                  className="control mt-1 w-full"
                  type={
                    field.type === 'number' || field.type === 'integer'
                      ? 'number'
                      : 'text'
                  }
                  step={field.type === 'integer' ? 1 : 'any'}
                  value={String(fields[key] ?? '')}
                  required={required.includes(key)}
                  disabled={disabled || busy}
                  onChange={(event) =>
                    setFields((current) => ({
                      ...current,
                      [key]:
                        field.type === 'number' || field.type === 'integer'
                          ? Number(event.target.value)
                          : event.target.value,
                    }))
                  }
                />
              )}
            </label>
          ))
        ) : (
          <>
            <details className="my-2">
              <summary>Operation fields</summary>
              <StructuredValue value={schema} />
            </details>
            <label className="block my-2">
              Parameters
              <textarea
                className="control mono mt-1 w-full text-xs"
                rows={3}
                value={text}
                onChange={(event) => setText(event.target.value)}
                disabled={disabled || busy}
              />
            </label>
          </>
        )}
        <button
          type="submit"
          className="btn btn-sm mt-2"
          disabled={disabled || busy}
        >
          {busy ? 'Running…' : 'Run operation'}
        </button>
      </form>
      {error && (
        <p role="alert" className="my-2 text-danger">
          {error}
        </p>
      )}
      {result !== undefined && <StructuredValue value={result} />}
    </details>
  )
}
